import { getCookie } from './Auth/getCookie';
import { mergeLatestClickIds } from './clickIds';
import {
  ANON_ID_COOKIE,
  getMarketingAnonId,
  isBareMarketingAnonId,
  readMarketingAnonIdCookie,
} from './marketingAnonId';

export { getMarketingAnonId };

const ATTRIBUTION_COOKIE = 'tt_attribution';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
] as const;

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
type AttributionCookie = Partial<Record<AttributionKey, string>> & {
  first_landing_page?: string;
  first_referrer?: string;
  first_seen_at?: string;
  last_landing_page?: string;
  last_referrer?: string;
  last_seen_at?: string;
};

const ATTRIBUTION_COOKIE_KEYS = new Set<string>([
  ...ATTRIBUTION_KEYS,
  'first_landing_page',
  'first_referrer',
  'first_seen_at',
  'last_landing_page',
  'last_referrer',
  'last_seen_at',
]);

function createAttributionCookie(): AttributionCookie {
  return Object.create(null) as AttributionCookie;
}

export interface AttributionData {
  attribution_source?: string;
  attribution_medium?: string;
  attribution_campaign?: string;
  attribution_id?: string;
  attribution_term?: string;
  attribution_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  first_touch_channel?: string;
  first_landing_page?: string;
  first_referrer?: string;
  first_seen_at?: string;
  marketing_anonymous_id?: string;
}

function getCookieDomain(): string | undefined {
  if (window.location.hostname === 'tokentax.co' || window.location.hostname.endsWith('.tokentax.co')) {
    return '.tokentax.co';
  }
  return undefined;
}

function isLocalhost() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

function writeCookie(name: string, value: string) {
  const domain = getCookieDomain();
  const domainAttr = domain ? `; domain=${domain}` : '';
  const secureAttr = isLocalhost() ? '' : '; Secure';
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${domainAttr}${secureAttr}`;
}

function parseAttributionCookie(raw: string | null): AttributionCookie {
  if (!raw) return {};

  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    return Object.entries(parsed).reduce<AttributionCookie>((acc, [key, value]) => {
      if (ATTRIBUTION_COOKIE_KEYS.has(key) && typeof value === 'string' && value) {
        acc[key as keyof AttributionCookie] = value;
      }
      return acc;
    }, createAttributionCookie());
  } catch {
    return {};
  }
}

function getCurrentAttributionParams(): Partial<Record<AttributionKey, string>> {
  const params = new URLSearchParams(window.location.search);
  const attribution: Partial<Record<AttributionKey, string>> = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (value) {
      attribution[key] = value;
    }
  }

  return attribution;
}

function attributionFromVisit(
  existing: AttributionCookie,
  current: Partial<Record<AttributionKey, string>>,
): AttributionCookie {
  const hasExistingAttribution = ATTRIBUTION_KEYS.some((key) => Boolean(existing[key]));
  const resolved: AttributionCookie = { ...existing };

  if (!hasExistingAttribution) {
    for (const key of ATTRIBUTION_KEYS) {
      if (current[key]) resolved[key] = current[key];
    }
  }

  mergeLatestClickIds(resolved, current);
  return resolved;
}

function getResolvedAttribution(): AttributionCookie {
  return attributionFromVisit(getAttributionCookie(), getCurrentAttributionParams());
}

function getAttributionCookie(): AttributionCookie {
  return parseAttributionCookie(getCookie(ATTRIBUTION_COOKIE));
}

function writeAttributionCookie(attribution: AttributionCookie) {
  writeCookie(ATTRIBUTION_COOKIE, JSON.stringify(attribution));
}

export function deriveFirstTouchChannel(attribution: Partial<Record<string, string>>): string | undefined {
  const source = attribution.utm_source?.toLowerCase();
  const medium = attribution.utm_medium?.toLowerCase();

  if (attribution.gclid || attribution.gbraid || attribution.wbraid) return 'google_ads';
  if (attribution.fbclid) return 'meta_ads';
  if (source && ['google', 'google_ads', 'adwords'].includes(source)) return 'google_ads';
  if (source && ['facebook', 'fb', 'instagram', 'ig', 'meta'].includes(source)) return 'meta_ads';
  if (medium && ['cpc', 'ppc', 'paid', 'display', 'retargeting', 'paid_social'].includes(medium)) {
    return source ?? 'paid_unknown';
  }
  if (source === 'email' || medium === 'email') return 'email';
  if (source === 'affiliate' || medium === 'affiliate') return 'affiliate';
  return undefined;
}

// The API re-issues both cookies as long-lived server cookies, and a script write
// would make Safari cap their lifetime again: write tt_attribution only when it
// changes, and tt_anon_id only when it is missing.
export function captureUtmParams() {
  const params = new URLSearchParams(window.location.search);
  const linkedAnonymousId = params.get(ANON_ID_COOKIE);
  if (linkedAnonymousId && isBareMarketingAnonId(linkedAnonymousId) && !readMarketingAnonIdCookie()) {
    writeCookie(ANON_ID_COOKIE, linkedAnonymousId);
  }

  const existing = getAttributionCookie();
  const merged = attributionFromVisit(existing, getCurrentAttributionParams());
  if (ATTRIBUTION_KEYS.every((key) => merged[key] === existing[key])) return;

  const now = new Date().toISOString();
  merged.first_landing_page = existing.first_landing_page ?? window.location.href;
  merged.first_referrer = existing.first_referrer ?? document.referrer;
  merged.first_seen_at = existing.first_seen_at ?? now;
  merged.last_landing_page = window.location.href;
  merged.last_referrer = document.referrer;
  merged.last_seen_at = now;

  writeAttributionCookie(merged);
}

export function setAnonIdCookie() {
  if (getCookie(ANON_ID_COOKIE)) return;

  const anonymousId = window.rudderanalytics?.getAnonymousId?.();
  if (anonymousId) {
    writeCookie(ANON_ID_COOKIE, anonymousId);
  }
}

/**
 * Reads the tt_attribution cookie set by the marketing site and returns
 * the attribution traits to include in identify() calls.
 */
export function getAttributionData(): AttributionData {
  const parsed = getResolvedAttribution();
  const data: AttributionData = {};

  if (parsed.utm_source) data.attribution_source = parsed.utm_source;
  if (parsed.utm_medium) data.attribution_medium = parsed.utm_medium;
  if (parsed.utm_campaign) data.attribution_campaign = parsed.utm_campaign;
  if (parsed.utm_id) data.attribution_id = parsed.utm_id;
  if (parsed.utm_term) data.attribution_term = parsed.utm_term;
  if (parsed.utm_content) data.attribution_content = parsed.utm_content;
  if (parsed.gclid) data.gclid = parsed.gclid;
  if (parsed.gbraid) data.gbraid = parsed.gbraid;
  if (parsed.wbraid) data.wbraid = parsed.wbraid;
  if (parsed.fbclid) data.fbclid = parsed.fbclid;
  if (parsed.first_landing_page) data.first_landing_page = parsed.first_landing_page;
  if (parsed.first_referrer) data.first_referrer = parsed.first_referrer;
  if (parsed.first_seen_at) data.first_seen_at = parsed.first_seen_at;

  const firstTouchChannel = deriveFirstTouchChannel(parsed);
  if (firstTouchChannel) data.first_touch_channel = firstTouchChannel;

  const marketingAnonymousId = getMarketingAnonId();
  if (marketingAnonymousId) data.marketing_anonymous_id = marketingAnonymousId;

  return data;
}

export function getAttributionProperties(): Record<string, string> {
  return Object.fromEntries(
    Object.entries({
      ...getAttributionCookie(),
      ...getCurrentAttributionParams(),
      ...getAttributionData(),
    }).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  );
}

export function getRudderStackPageOptions(): Record<string, unknown> | undefined {
  const attribution = getAttributionProperties();
  const source =
    attribution.utm_source ??
    (attribution.gclid || attribution.gbraid || attribution.wbraid ? 'google' : undefined);
  const medium = attribution.utm_medium ?? (source === 'google' ? 'cpc' : undefined);

  if (
    !source &&
    !medium &&
    !attribution.utm_campaign &&
    !attribution.utm_id &&
    !attribution.utm_term &&
    !attribution.utm_content
  ) {
    return undefined;
  }

  return {
    context: {
      campaign: {
        ...(source ? { source } : {}),
        ...(medium ? { medium } : {}),
        ...(attribution.utm_campaign ? { name: attribution.utm_campaign } : {}),
        ...(attribution.utm_id ? { id: attribution.utm_id } : {}),
        ...(attribution.utm_term ? { term: attribution.utm_term } : {}),
        ...(attribution.utm_content ? { content: attribution.utm_content } : {}),
      },
    },
  };
}

export function getAttributionMetadata() {
  const attribution = getResolvedAttribution();
  const marketingAnonymousId = getMarketingAnonId();

  return {
    ...(attribution.utm_source ? { attributionSource: attribution.utm_source } : {}),
    ...(attribution.utm_medium ? { attributionMedium: attribution.utm_medium } : {}),
    ...(attribution.utm_campaign ? { attributionCampaign: attribution.utm_campaign } : {}),
    ...(attribution.gclid ? { gclid: attribution.gclid } : {}),
    ...(attribution.gbraid ? { gbraid: attribution.gbraid } : {}),
    ...(attribution.wbraid ? { wbraid: attribution.wbraid } : {}),
    ...(attribution.fbclid ? { fbclid: attribution.fbclid } : {}),
    ...(marketingAnonymousId ? { marketingAnonymousId } : {}),
  };
}
